/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */
import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import PointMessage from '@/core/models/ed-task-board/point-message.model';
import {BOARD_CONFIG_PARAMETERS} from '@/values/ed-whiteboard.values';
import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import EdWboDrawerPencilPoints
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/pencil/ed-wbo-drawer-pencil-points';

export default class EdWboDrawerPencil {

    private wboDrawerTools: WboDrawerTools;
    private readonly MIN_PENCIL_INTERVAL_MS;
    private svg: SVGSVGElement;
    private MAX_PENCIL_POINTS : number;

    //Indicates the id of the line the user is currently drawing or an empty string while the user is not drawing
    private curLineId: any = '';
    private lastTime = performance.now(); //The time at which the last point was drawn
    private renderingLine: any = {};
    private readonly boardParameters: { [key: string]: any } = {};
    private lastBoundedX: number;
    private lastBoundedY: number;
    private setCursorBoardMutation: any;

    constructor(tools: WboDrawerTools, setCursorBoardMutation: any = null) {
        this.wboDrawerTools = tools;
        this.setCursorBoardMutation = setCursorBoardMutation;
        this.boardParameters = this.wboDrawerTools.boardParameters;
        // Allocate the full maximum server update rate to pencil messages.
        // This feels a bit risky in terms of dropped messages, but any less
        // gives terrible results with the default parameters.  In practice it
        // seems to work, either because writing tends to happen in bursts, or
        // maybe because the messages are sent when the time interval is *greater*
        // than this?
        this.MIN_PENCIL_INTERVAL_MS = this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT_PERIOD] / this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT];
        this.svg = this.wboDrawerTools.svg;
        this.wboDrawerTools.add(this.pencilTool);
        this.MAX_PENCIL_POINTS = Number.parseInt(this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_PENCIL_POINTS]);
    }

    private createLine = (lineData) => {
        //Creates a new line on the canvas, or update a line that already exists with new information
        this.removePencilPathDataCacheById(lineData.id);
        const existingLine = this.svg.getElementById(lineData.id);
        if (existingLine) {
            this.wboDrawerTools.drawingArea.removeChild(existingLine);
        }

        let line = this.wboDrawerTools.createSVGElement('path');
        line.id = lineData.id;
        //If some data is not provided, choose default value. The line may be updated later
        line.setAttribute('stroke', lineData.color || 'black');
        line.setAttribute('stroke-width', lineData.size || 10);
        line.setAttribute('opacity', String(Math.max(0.1, Math.min(1, lineData.opacity)) || 1));
        lineData.transform && line.setAttribute('transform', lineData.transform);
        this.wboDrawerTools.addAnnotationOwners(line, lineData);
        this.wboDrawerTools.drawingArea.appendChild(line);
        return line;
    }

    private continueLine = (x: number, y: number, evt?: any) => {
        /*Wait 70ms before adding any point to the currently drawing line.
        This allows the animation to be smoother*/
        if (this.curLineId !== '') {
            if (this.isWithinBounds(x, y)) {
                this.lastBoundedX = x;
                this.lastBoundedY = y;
            }
        }
        if (this.curLineId !== '' && performance.now() - this.lastTime > this.MIN_PENCIL_INTERVAL_MS) {
            this.wboDrawerTools.drawAndSend(new PointMessage(x, y, this.curLineId));
            this.lastTime = performance.now();
        }
        evt?.preventDefault();
    }

    private startLine = (x: number, y: number, evt: MouseEvent) => {
        //Prevent the press from being interpreted by the browser
        evt?.preventDefault();

        if(this.setCursorBoardMutation) {
            this.setCursorBoardMutation(false);
        }

        this.curLineId = this.wboDrawerTools.generateUID('web_l'); //'l' for line
        this.wboDrawerTools.drawAndSend({
            type   : 'line',
            id     : this.curLineId,
            color  : (this.pencilTool.secondary.active ? '#ffffff' : this.wboDrawerTools.getColor()),
            size   : this.wboDrawerTools.getSize(),
            opacity: (this.pencilTool.secondary.active ? 1 : this.wboDrawerTools.getOpacity())
        });

        //Immediatly add a point to the line
        this.continueLine(x, y);
    }
    private stopLineAt = (x: number, y: number, evt?: MouseEvent) => {
        //Add a last point to the line
        if (this.curLineId) {
            let finalX = x;
            let finalY = y;
            if (!this.isWithinBounds(x, y)) {
                const boundedPoint = this.getEdgeIntersectingPoint({x: this.lastBoundedX, y: this.lastBoundedY}, {x, y});
                finalX = boundedPoint.x;
                finalY = boundedPoint.y;
            }
            this.wboDrawerTools.drawAndSend(new PointMessage(finalX, finalY, this.curLineId));
            this.wboDrawerTools.send(this.pencilTool, { type: 'finish', parent: this.curLineId, x: finalX, y: finalY });
        }
        this.stopLine();
        if(this.setCursorBoardMutation) {
            this.setCursorBoardMutation(true);
        }
    }

    private stopLine = () => {
        if (this.curLineId && this.wboDrawerTools.stopStrokeCallback) {
            this.wboDrawerTools.stopStrokeCallback()
        }
        this.curLineId = '';
    }

    private draw = (data, isMadeByUser: boolean = true) => {
        this.wboDrawerTools.drawingEvent = true;
        switch (data.type) {
            case 'line':
                this.renderingLine = this.createLine(data);
                break;
            case 'child': {
                let line = (this.renderingLine.id === data.parent) ? this.renderingLine : this.svg.getElementById(data.parent);
                if (!line) {
                    line = this.renderingLine = this.createLine({'id': data.parent}); //create a new line in order not to loose the points
                }

                let pts = this.getPathData(line);
                if (((pts.length + 1) === this.MAX_PENCIL_POINTS) && isMadeByUser) {
                    this.addPoint(line, data.x, data.y, data.flushPathData);
                    this.wboDrawerTools.send(this.pencilTool, new PointMessage(data.x, data.y, line.id));
                    this.wboDrawerTools.send(this.pencilTool, {
                        type: 'finish',
                        parent: this.curLineId,
                        x: data.x,
                        y: data.y
                    }, isMadeByUser);
                    this.startLine(data.x, data.y, null);
                    return false;
                }
                else {
                    this.addPoint(line, data.x, data.y, data.flushPathData);
                }
                break;
            }
            case 'endline':
            case 'finish': {
                let line = (this.renderingLine.id === data.parent) ? this.renderingLine : this.svg.getElementById(data.parent);
                if (line) {
                    line.setPathData(this.getPathData(line));
                }
                break;
            }
            default:
                break;
        }
        return true;
    }

    private pathDataCache = {};

    private getPathData = (line) => {
        let pathData = this.pathDataCache[line.id];
        if (!pathData) {
            pathData = line.getPathData();
            this.pathDataCache[line.id] = pathData;
        }
        return pathData;
    }

    private addPoint = (line, x, y, flushPathData = true) => {
        let pts = this.getPathData(line);
        pts = EdWboDrawerPencilPoints.wboPencilPoint(pts, x, y);
        if (flushPathData) {
            line.setPathData(pts);
        }

    }

    private isWithinBounds(x: number, y: number) {
        const maxWidth = this.wboDrawerTools.svg.width.baseVal.value;
        const maxHeight = this.wboDrawerTools.svg.height.baseVal.value;
        return x >= 0 && x <= maxWidth && y >= 0 && y <= maxHeight;
    }

    public clearPencilPathDataCache() {
        this.pathDataCache = {};
    }

    public removePencilPathDataCacheById(id: string) {
        if (this.pathDataCache[id]) {
            delete this.pathDataCache[id];
        }
    }

    /**
     * This function calculates an X, Y point that intersects the board's bounds between one point that's within the
     * bounds and another one that is outside.
     *
     */
    private getEdgeIntersectingPoint(pointInside: { x: number, y: number }, pointOutside: { x: number, y: number }): {
        x: number,
        y: number
    } {
        const maxWidth = this.wboDrawerTools.svg.width.baseVal.value;
        const maxHeight = this.wboDrawerTools.svg.height.baseVal.value;

        const dx = pointOutside.x - pointInside.x;
        if (dx == 0) {
            // Vertical line
            return {x: pointInside.x, y: pointOutside.y < pointInside.y ? 0 : maxHeight};
        }

        const dy = pointOutside.y - pointInside.y;
        if (dy == 0) {
            // Horizontal line
            return {x: pointOutside.x < pointInside.x ? 0 : maxWidth, y: pointInside.y};
        }

        // Find the slope and y-intercept of the line between the two points
        const m = dy / dx;
        const b = pointInside.y - m * pointInside.x;

        const outsideRect = !this.isWithinBounds(pointOutside.x, pointOutside.y);

        // Find the intersection point with the rectangle
        if (outsideRect) {
            let x, y;

            // See if it intersects with the left edge
            x = 0;
            y = b;
            if (y >= 0 && y <= maxHeight && dx < 0) {
                return {x, y};
            }

            // See if it intersects with the right edge
            x = maxWidth;
            y = m * x + b;
            if (y >= 0 && y <= maxHeight && dx > 0) {
                return {x, y};
            }

            // See if it intersects with the top edge
            x = -b / m;
            y = 0;
            if (x >= 0 && x <= maxWidth && dy < 0) {
                return {x, y};
            }

            // See if it intersects with the bottom edge
            x = (maxHeight - b) / m;
            y = maxHeight;
            if (x >= 0 && x <= maxWidth && dy > 0) {
                return {x, y};
            }

        } else {
            // If the second point is inside the rectangle, return its coordinates
            return {x: pointOutside.x, y: pointOutside.y};
        }

    }

    private pencilTool: BoardTool = {
        name       : 'Pencil',
        shortcut   : 'p',
        listeners  : {
            press  : this.startLine,
            move   : this.continueLine,
            release: this.stopLineAt
            // ,leave  : this.forcefullyStopLineAt
            // ,out    : this.forcefullyStopLineAt
        },
        draw       : this.draw,
        secondary  : {
            name  : 'White-out',
            icon  : '/assets/tools/pencil/whiteout_tape.svg',
            active: false,
            switch: this.stopLine
        },
        // This is the base64 of /public/wbo/assets/tools/pencil/cursor.svg
        mouseCursor: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIz' +
            'MCIgaGVpZ2h0PSIzMCIgdmlld0JveD0iMCAwIDYwIDYwIj4KICA8ZGVmcy8+CiAgPGcgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjMDAwIiBz' +
            'dHJva2Utd2lkdGg9IjIiPgogICAgPHBhdGggZD0iTTQxLjA2IDMzLjgzYy42LTIuOC00LjUyLTUuMDQtNC42NS01LjFsLTIxLTIzLjAz' +
            'TDMuMTggMTYuODNsMjEgMjMuMDR2LjAyczEuODQgNS40OCA0LjY1IDUuMDlhMTcuODUgMTcuODUgMCAwMDEyLjIzLTExLjE0eiIvPgog' +
            'ICAgPHBhdGggZmlsbD0iIzAwMCIgc3Ryb2tlPSJub25lIiBkPSJNMS4xOCAxLjM5bC42OCA1Ljk3IDQuOTctNC43My01LjY5LTEuMjYi' +
            'Lz4KICAgIDxwYXRoIGQ9Ik0zMi41IDMyLjM4TDExLjc2IDkuNDhNMjguMzMgMzYuM0w3LjM1IDEzLjM5TTE1LjIxIDUuMjZMMS4xIDEu' +
            'NDNsMS42OCAxNS4yMk0zNi40MSAyOC43NGwtMTEuOCAxMS42MmgwIi8+CiAgPC9nPgo8L3N2Zz4="), auto',
        icon       : '/assets/tools/pencil/icon.svg'
    };
}

